import assign from 'lodash/assign';
import { DateTime } from 'luxon';
import type { ILocation } from '../../models/location';
import type { LocationType } from '../../models/location-type.enum';
import { ConfigurationService } from '../configuration.service';
import { IServiceBaseError, OauthHttpClientWrapper, ServiceBase } from '../service-base.service';
import { LocationApiModel } from './models/locations/location.api-model';
import { TransportMode } from '../../models/transport-mode.enum';
import type { TransportSubMode } from '../../models/transport-sub-mode.enum';
import { DateTimeRelatesTo } from '../../models/date-time-relates-to.enum';
import type { IPaginationResult } from '../../models/pagination-result.model';
import type { IJourney } from '../../models/journey.model';
import { JourneyApiModel } from './models/journeys/journey.api-model';
import { DepartureResultApiModel } from './models/departures-and-arrivals/departure-result.api-model';
import type { IDeparture } from '../../models/departure.model';
import { ErrorHandlerHelper } from '../../helpers/error-handler.helper';
import { stringify } from 'qs';
import PaginationResultApiModel from '../pagination-result.api-model';
import PaginationLinksApiModel from '../pagination-links.api-model';

export class ApiPlaneraResaService extends ServiceBase {
  public static get(url: string): Promise<unknown> {
    return this.getWithTryGetNewToken<unknown>(ApiPlaneraResaService.httpClientWrapper, url);
  }

  public static getLocationsByCoordinates(latitude: number, longitude: number, radiusInMeters: number, types: LocationType[], limit: number): Promise<ILocation[]> {
    const params = { latitude, longitude, radiusInMeters, types, limit };
    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<PaginationResultApiModel<LocationApiModel>>(ApiPlaneraResaService.httpClientWrapper, '/locations/by-coordinates', new URLSearchParams(stringify(params)))
        .then((response) => resolve(response.results.map(LocationApiModel.mapToBusinessModel)))
        .catch((error: IServiceBaseError) => reject(error));
    });
  }

  public static getHistoricJourneys(originGid: string, destinationGid: string, dateTime: DateTime, dateTimeRelatesTo: DateTimeRelatesTo): Promise<IPaginationResult<IJourney>> {
    const params = {
      originGid,
      destinationGid,
      dateTime: dateTime.toISO(),
      dateTimeRelatesTo,
    };
    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<PaginationResultApiModel<JourneyApiModel>>(ApiPlaneraResaService.httpClientWrapper, '/journeys', new URLSearchParams(stringify(params)))
        .then((response) => resolve(this.filterOnlyHistoricJourneysAndUpdatePagination(this.mapPaginationJourneyApiModelToBusinessModel(response))))
        .catch((error: IServiceBaseError) => reject(ErrorHandlerHelper.mapErrorToIErrorResponse(error)));
    });
  }

  public static getJourneyByReconstructionReference(reference: string): Promise<IJourney> {
    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<JourneyApiModel>(ApiPlaneraResaService.httpClientWrapper, '/journeys/reconstruct', new URLSearchParams(stringify({ ref: reference })))
        .then((response) => resolve(JourneyApiModel.mapToBusinessModel(response)))
        .catch((error: IServiceBaseError) => reject(ErrorHandlerHelper.mapErrorToIErrorResponse(error)));
    });
  }

  public static getJourneys(
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined,
    dateTime: DateTime | undefined,
    dateTimeRelatesTo: DateTimeRelatesTo,
    paginationReference: string | undefined,
    limit: number | undefined,
    transportModes: TransportMode[] | undefined,
    transportSubModes: TransportSubMode[] | undefined,
    onlyDirectConnections: boolean | undefined,
    viaGid: string | undefined,
    interchangeDurationInMinutes: number | undefined,
    nearbyStopAreas: boolean | undefined,
    otherTransportModes: TransportMode[] | undefined,
    bikeMaxDistanceInMeters: number | undefined,
    bikeSpeedInKmPerHour: number | undefined,
    useOccupancy: boolean | undefined,
  ): Promise<IPaginationResult<IJourney>> {
    const params = this.getJourneyParams(
      originGid,
      originName,
      originLatitude,
      originLongitude,
      destinationGid,
      destinationName,
      destinationLatitude,
      destinationLongitude,
      dateTime,
      dateTimeRelatesTo,
      paginationReference,
      limit,
      transportModes,
      transportSubModes,
      onlyDirectConnections,
      viaGid,
      interchangeDurationInMinutes,
      nearbyStopAreas,
      otherTransportModes,
      bikeMaxDistanceInMeters,
      bikeSpeedInKmPerHour,
      useOccupancy,
    );

    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<PaginationResultApiModel<JourneyApiModel>>(ApiPlaneraResaService.httpClientWrapper, '/journeys', new URLSearchParams(stringify(params)))
        .then((response) => resolve(this.mapPaginationJourneyApiModelToBusinessModel(response)))
        .catch((error: IServiceBaseError) => {
          reject(ErrorHandlerHelper.mapErrorToIErrorResponse(error));
        });
    });
  }

  public static searchJourneys({
    originGid,
    originName,
    originLatitude,
    originLongitude,
    destinationGid,
    destinationName,
    destinationLatitude,
    destinationLongitude,
    dateTime,
    dateTimeRelatesTo,
    paginationReference,
    limit,
    transportModes,
    transportSubModes,
    onlyDirectConnections,
    viaGid,
    interchangeDurationInMinutes,
    nearbyStopAreas,
    otherTransportModes,
    bikeMaxDistanceInMeters,
    bikeSpeedInKmPerHour,
    useOccupancy,
  }: {
    originGid?: string | undefined;
    originName?: string | undefined;
    originLatitude?: number | undefined;
    originLongitude?: number | undefined;
    destinationGid?: string | undefined;
    destinationName?: string | undefined;
    destinationLatitude?: number | undefined;
    destinationLongitude?: number | undefined;
    dateTime?: DateTime | undefined;
    dateTimeRelatesTo?: DateTimeRelatesTo | undefined;
    paginationReference?: string | undefined;
    limit?: number | undefined;
    transportModes?: TransportMode[] | undefined;
    transportSubModes?: TransportSubMode[] | undefined;
    onlyDirectConnections?: boolean | undefined;
    viaGid?: string | undefined;
    interchangeDurationInMinutes?: number | undefined;
    nearbyStopAreas?: boolean | undefined;
    otherTransportModes?: TransportMode[] | undefined;
    bikeMaxDistanceInMeters?: number | undefined;
    bikeSpeedInKmPerHour?: number | undefined;
    useOccupancy?: boolean | undefined;
  }): Promise<IPaginationResult<IJourney>> {
    return ApiPlaneraResaService.getJourneys(
      originGid,
      originName,
      originLatitude,
      originLongitude,
      destinationGid,
      destinationName,
      destinationLatitude,
      destinationLongitude,
      dateTime,
      dateTimeRelatesTo ?? DateTimeRelatesTo.departure,
      paginationReference,
      limit,
      transportModes,
      transportSubModes,
      onlyDirectConnections,
      viaGid,
      interchangeDurationInMinutes,
      nearbyStopAreas,
      otherTransportModes,
      bikeMaxDistanceInMeters,
      bikeSpeedInKmPerHour,
      useOccupancy,
    );
  }

  public static getJourneysFromPagination(paginationLink: string): Promise<IPaginationResult<IJourney>> {
    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<PaginationResultApiModel<JourneyApiModel>>(ApiPlaneraResaService.httpClientWrapper, `/${paginationLink}`)
        .then((response) => resolve(this.mapPaginationJourneyApiModelToBusinessModel(response)))
        .catch((error: IServiceBaseError) => reject(ErrorHandlerHelper.mapErrorToIErrorResponse(error)));
    });
  }

  public static getDepartures(stopAreaGid: string, limit: number): Promise<IDeparture[]> {
    return new Promise((resolve, reject) => {
      this.getWithTryGetNewToken<DepartureResultApiModel>(ApiPlaneraResaService.httpClientWrapper, `/stop-areas/${stopAreaGid}/departures?limit=${limit}`)
        .then((response) => resolve(DepartureResultApiModel.mapToBusinessModel(response)))
        .catch((error: IServiceBaseError) => reject(ErrorHandlerHelper.mapErrorToIErrorResponse(error)));
    });
  }

  private static httpClientWrapper: OauthHttpClientWrapper = new OauthHttpClientWrapper(
    ConfigurationService.get('apiJourneyPlannerBaseURL') ??
      (() => {
        throw new Error('API Journey Planner Base URL is not set');
      })(),
  );

  private static getJourneyParams(
    originGid: string | undefined,
    originName: string | undefined,
    originLatitude: number | undefined,
    originLongitude: number | undefined,
    destinationGid: string | undefined,
    destinationName: string | undefined,
    destinationLatitude: number | undefined,
    destinationLongitude: number | undefined,
    dateTime: DateTime | undefined,
    dateTimeRelatesTo: DateTimeRelatesTo,
    paginationReference: string | undefined,
    limit: number | undefined,
    transportModes: TransportMode[] | undefined,
    transportSubModes: TransportSubMode[] | undefined,
    onlyDirectConnections: boolean | undefined,
    viaGid: string | undefined,
    interchangeDurationInMinutes: number | undefined,
    nearbyStopAreas: boolean | undefined,
    otherTransportModes: TransportMode[] | undefined,
    bikeMaxDistanceInMeters: number | undefined,
    bikeSpeedInKmPerHour: number | undefined,
    useOccupancy: boolean | undefined,
  ): unknown {
    const params = { dateTimeRelatesTo };

    if (originGid) {
      assign(params, { originGid });
    } else if (originName && originLatitude && originLongitude) {
      assign(params, { originName, originLatitude, originLongitude });
    }
    if (destinationGid) {
      assign(params, { destinationGid });
    } else if (destinationName && destinationLatitude && destinationLongitude) {
      assign(params, { destinationName, destinationLatitude, destinationLongitude });
    }
    if (dateTime) {
      assign(params, { dateTime: dateTime.toISO() });
    }
    if (paginationReference) {
      assign(params, { paginationReference });
    }
    if (limit) {
      assign(params, { limit });
    }
    if (transportModes) {
      assign(params, { transportModes });
    }
    if (transportSubModes) {
      assign(params, { transportSubModes });
    }
    if (onlyDirectConnections) {
      assign(params, { onlyDirectConnections });
    }
    if (viaGid) {
      assign(params, { viaGid });
    }
    if (interchangeDurationInMinutes) {
      assign(params, { interchangeDurationInMinutes });
    }
    if (useOccupancy) {
      assign(params, { includeOccupancy: useOccupancy });
    }
    if (nearbyStopAreas) {
      assign(params, { includeNearbyStopAreas: nearbyStopAreas });
    }
    // Show bike journey between 500 - maxTotalDistanceBikeInMeters(5000) meter - NV-3567
    if (otherTransportModes?.some((tm) => tm === TransportMode.bike)) {
      assign(params, { totalBike: `1,500,${bikeMaxDistanceInMeters || 0},${bikeSpeedInKmPerHour || 0}` });
    }
    return params;
  }

  private static mapPaginationJourneyApiModelToBusinessModel(model: PaginationResultApiModel<JourneyApiModel>): IPaginationResult<IJourney> {
    return {
      results: model.results ? model.results.map((x) => JourneyApiModel.mapToBusinessModel(x)) : [],
      totalCount: model.totalCount,
      count: model.count,
      links: model.links ? PaginationLinksApiModel.mapToBusinessModel(model.links) : undefined,
    };
  }

  private static filterOnlyHistoricJourneysAndUpdatePagination(result: IPaginationResult<IJourney>): IPaginationResult<IJourney> {
    if (!result || !result.results) {
      return result;
    }

    // Only show the next pagination link if the last journey is historic
    const lastJourney = result.results[result.results.length - 1];
    if (result.links) {
      result.links.next = this.isJourneyHistoric(lastJourney) ? result.links.next : undefined;
    }
    const historicJourneys = result.results.filter((journey) => this.isJourneyHistoric(journey));
    result.results = historicJourneys;

    return result;
  }

  private static isJourneyHistoric(journey: IJourney): boolean {
    if (!journey || !journey.tripLegs) {
      return false;
    }

    if (!journey.tripLegs) {
      return false;
    }

    const lastPublicTransportTripLegs = journey.tripLegs[journey.tripLegs.length - 1];
    return lastPublicTransportTripLegs.plannedArrivalTime ? DateTime.now() >= lastPublicTransportTripLegs.plannedArrivalTime : false;
  }
}
